import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { DataAndReporting } from '../types';

// Firestore collection names
const TRACKING_EVENTS_COLLECTION = 'trackingEvents';
const STUDENT_PROGRESS_COLLECTION = 'studentProgress';
const ANALYTICS_COLLECTION = 'analytics';

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  return db && process.env.REACT_APP_FIREBASE_API_KEY && 
         process.env.REACT_APP_FIREBASE_API_KEY !== 'your_api_key_here';
};

export interface TrackingEvent {
  id?: string; // Optional for Firestore auto-generation
  lessonId: string;
  studentId: string;
  eventType: string;
  timestamp: Timestamp | Date;
  data: Record<string, any>;
  sessionId: string; // Now required since we always provide a value
}

export interface StudentProgress {
  id?: string; // Optional for Firestore auto-generation
  studentId: string;
  lessonId: string;
  stepId?: string;
  completed: boolean;
  timeSpent: number;
  score?: number;
  interactions: number;
  lastActivity: Timestamp | Date;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface AnalyticsData {
  id?: string; // Optional for Firestore auto-generation
  lessonId: string;
  sessionId: string;
  studentId: string;
  summary: {
    totalTime: number;
    completedSteps: number;
    totalSteps: number;
    averageScore?: number;
    engagementScore: number;
  };
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

class TrackingService {
  // For backward compatibility when Firebase is not configured
  private fallbackEvents: TrackingEvent[] = [];
  private fallbackProgress: Map<string, StudentProgress> = new Map();
  private fallbackAnalytics: Map<string, AnalyticsData> = new Map();

  // Generate unique IDs
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Track a new event
  async trackEvent(
    lessonId: string,
    studentId: string,
    eventType: string,
    data: Record<string, any> = {},
    sessionId?: string
  ): Promise<TrackingEvent> {
    // Generate a session ID if none provided
    const validSessionId = sessionId || this.generateId();
    
    const event: TrackingEvent = {
      lessonId,
      studentId,
      eventType,
      timestamp: Timestamp.now(),
      data,
      sessionId: validSessionId
    };

    try {
      if (isFirebaseConfigured()) {
        // Add to Firestore
        const docRef = await addDoc(collection(db, TRACKING_EVENTS_COLLECTION), event);
        event.id = docRef.id;
        
        // Update progress
        await this.updateProgress(lessonId, studentId, eventType, data);
      } else {
        // Fallback to local storage
        event.id = this.generateId();
        event.timestamp = new Date();
        this.fallbackEvents.push(event);
        this.updateProgressFallback(lessonId, studentId, eventType, data);
        this.persistEvents();
      }
    } catch (error) {
      console.error('Error tracking event:', error);
      // Fallback to local storage on error
      event.id = this.generateId();
      event.timestamp = new Date();
      this.fallbackEvents.push(event);
      this.updateProgressFallback(lessonId, studentId, eventType, data);
      this.persistEvents();
    }
    
    return event;
  }

  // Update student progress based on events (Firebase version)
  private async updateProgress(
    lessonId: string,
    studentId: string,
    eventType: string,
    data: Record<string, any>
  ): Promise<void> {
    try {
      // Find existing progress document
      const progressQuery = query(
        collection(db, STUDENT_PROGRESS_COLLECTION),
        where('lessonId', '==', lessonId),
        where('studentId', '==', studentId)
      );
      
      const progressSnapshot = await getDocs(progressQuery);
      let currentProgress: StudentProgress;
      let progressDocId: string | undefined;

      if (!progressSnapshot.empty) {
        const progressDoc = progressSnapshot.docs[0];
        currentProgress = { id: progressDoc.id, ...progressDoc.data() } as StudentProgress;
        progressDocId = progressDoc.id;
      } else {
        currentProgress = {
          studentId,
          lessonId,
          completed: false,
          timeSpent: 0,
          interactions: 0,
          lastActivity: Timestamp.now(),
          createdAt: Timestamp.now()
        };
      }

      // Update based on event type
      switch (eventType) {
        case 'step_complete':
          currentProgress.stepId = data.stepId || ''; // Provide default empty string if stepId is undefined
          currentProgress.completed = data.completed || false;
          currentProgress.timeSpent += data.timeSpentSeconds || 0;
          break;
        
        case 'assessment_complete':
          currentProgress.score = data.score || 0; // Provide default value of 0 if score is undefined
          currentProgress.completed = true;
          currentProgress.timeSpent += data.timeSpentSeconds || 0;
          break;
        
        case 'resource_interaction':
          currentProgress.interactions += 1;
          currentProgress.timeSpent += isNaN(data.durationSeconds) ? 0 : data.durationSeconds;
          break;
        
        case 'quiz_response':
          currentProgress.interactions += 1;
          currentProgress.timeSpent += data.timeSpentSeconds || 0;
          break;
        
        case 'engagement_check':
          currentProgress.interactions += 1;
          break;
        
        default:
          currentProgress.interactions += 1;
          currentProgress.timeSpent += data.timeSpentSeconds || 0;
      }

      currentProgress.lastActivity = Timestamp.now();
      currentProgress.updatedAt = Timestamp.now();

      // Save to Firestore
      if (progressDocId) {
        await updateDoc(doc(db, STUDENT_PROGRESS_COLLECTION, progressDocId), currentProgress as any);
      } else {
        const docRef = await addDoc(collection(db, STUDENT_PROGRESS_COLLECTION), currentProgress as any);
        currentProgress.id = docRef.id;
      }
      
      // Update analytics
      await this.updateAnalytics(lessonId, studentId, currentProgress);
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  }

  // Fallback method for local storage
  private updateProgressFallback(
    lessonId: string,
    studentId: string,
    eventType: string,
    data: Record<string, any>
  ): void {
    const key = `${lessonId}-${studentId}`;
    const currentProgress = this.fallbackProgress.get(key) || {
      studentId,
      lessonId,
      completed: false,
      timeSpent: 0,
      interactions: 0,
      lastActivity: new Date()
    };

    // Update based on event type (same logic as Firebase version)
    switch (eventType) {
              case 'step_complete':
          currentProgress.stepId = data.stepId || ''; // Provide default empty string if stepId is undefined
          currentProgress.completed = data.completed || false;
          currentProgress.timeSpent += data.timeSpentSeconds || 0;
          break;
      
      case 'assessment_complete':
        currentProgress.score = data.score || 0; // Provide default value of 0 if score is undefined
        currentProgress.completed = true;
        currentProgress.timeSpent += data.timeSpentSeconds || 0;
        break;
      
      case 'resource_interaction':
        currentProgress.interactions += 1;
        currentProgress.timeSpent += data.durationSeconds || 0;
        break;
      
      case 'quiz_response':
        currentProgress.interactions += 1;
        currentProgress.timeSpent += data.timeSpentSeconds || 0;
        break;
      
      case 'engagement_check':
        currentProgress.interactions += 1;
        break;
      
      default:
        currentProgress.interactions += 1;
        currentProgress.timeSpent += data.timeSpentSeconds || 0;
    }

    currentProgress.lastActivity = new Date();
    this.fallbackProgress.set(key, currentProgress);
    
    // Update analytics
    this.updateAnalyticsFallback(lessonId, studentId, currentProgress);
    
    // Persist progress
    this.persistProgress();
  }

  // Update analytics data (Firebase version)
  private async updateAnalytics(
    lessonId: string,
    studentId: string,
    progress: StudentProgress
  ): Promise<void> {
    try {
      // Find existing analytics document
      const analyticsQuery = query(
        collection(db, ANALYTICS_COLLECTION),
        where('lessonId', '==', lessonId),
        where('studentId', '==', studentId)
      );
      
      const analyticsSnapshot = await getDocs(analyticsQuery);
      let analyticsDocId: string | undefined;

      if (!analyticsSnapshot.empty) {
        analyticsDocId = analyticsSnapshot.docs[0].id;
      }

      const analytics: AnalyticsData = {
        lessonId,
        sessionId: this.generateId(),
        studentId,
        summary: {
          totalTime: progress.timeSpent,
          completedSteps: progress.completed ? 1 : 0,
          totalSteps: 1, // This would be calculated from lesson timeline
          averageScore: progress.score || 0, // Provide default value of 0 if score is undefined
          engagementScore: Math.min(progress.interactions / 10, 1) // Normalize to 0-1
        },
        updatedAt: Timestamp.now()
      };

      if (!analytics.createdAt) {
        analytics.createdAt = Timestamp.now();
      }

      // Save to Firestore
      if (analyticsDocId) {
        await updateDoc(doc(db, ANALYTICS_COLLECTION, analyticsDocId), analytics as any);
      } else {
        const docRef = await addDoc(collection(db, ANALYTICS_COLLECTION), analytics as any);
        analytics.id = docRef.id;
      }
    } catch (error) {
      console.error('Error updating analytics:', error);
    }
  }

  // Fallback analytics method for local storage
  private updateAnalyticsFallback(
    lessonId: string,
    studentId: string,
    progress: StudentProgress
  ): void {
    const key = `${lessonId}-${studentId}`;

    const analytics: AnalyticsData = {
      lessonId,
      sessionId: this.generateId(),
      studentId,
      summary: {
        totalTime: progress.timeSpent,
        completedSteps: progress.completed ? 1 : 0,
        totalSteps: 1, // This would be calculated from lesson timeline
        averageScore: progress.score || 0, // Provide default value of 0 if score is undefined
        engagementScore: Math.min(progress.interactions / 10, 1) // Normalize to 0-1
      }
    };

    this.fallbackAnalytics.set(key, analytics);
    this.persistAnalytics();
  }

  // Get student progress
  async getStudentProgress(lessonId: string, studentId: string): Promise<StudentProgress | null> {
    try {
      if (isFirebaseConfigured()) {
        const progressQuery = query(
          collection(db, STUDENT_PROGRESS_COLLECTION),
          where('lessonId', '==', lessonId),
          where('studentId', '==', studentId)
        );
        
        const progressSnapshot = await getDocs(progressQuery);
        if (!progressSnapshot.empty) {
          const progressDoc = progressSnapshot.docs[0];
          return { id: progressDoc.id, ...progressDoc.data() } as StudentProgress;
        }
        return null;
      } else {
        // Fallback to local storage
        const key = `${lessonId}-${studentId}`;
        return this.fallbackProgress.get(key) || null;
      }
    } catch (error) {
      console.error('Error getting student progress:', error);
      // Fallback to local storage on error
      const key = `${lessonId}-${studentId}`;
      return this.fallbackProgress.get(key) || null;
    }
  }

  // Get analytics data
  async getAnalytics(lessonId: string, studentId: string): Promise<AnalyticsData | null> {
    try {
      if (isFirebaseConfigured()) {
        const analyticsQuery = query(
          collection(db, ANALYTICS_COLLECTION),
          where('lessonId', '==', lessonId),
          where('studentId', '==', studentId)
        );
        
        const analyticsSnapshot = await getDocs(analyticsQuery);
        if (!analyticsSnapshot.empty) {
          const analyticsDoc = analyticsSnapshot.docs[0];
          return { id: analyticsDoc.id, ...analyticsDoc.data() } as AnalyticsData;
        }
        return null;
      } else {
        // Fallback to local storage
        const key = `${lessonId}-${studentId}`;
        return this.fallbackAnalytics.get(key) || null;
      }
    } catch (error) {
      console.error('Error getting analytics:', error);
      // Fallback to local storage on error
      const key = `${lessonId}-${studentId}`;
      return this.fallbackAnalytics.get(key) || null;
    }
  }

  // Get all events for a lesson
  async getLessonEvents(lessonId: string, startDate?: Date, endDate?: Date): Promise<TrackingEvent[]> {
    try {
      if (isFirebaseConfigured()) {
        const eventsQuery = query(
          collection(db, TRACKING_EVENTS_COLLECTION),
          where('lessonId', '==', lessonId),
          ...(startDate ? [where('timestamp', '>=', startDate)] : []),
          ...(endDate ? [where('timestamp', '<=', endDate)] : []),
          orderBy('timestamp', 'desc')
        );
        
        const eventsSnapshot = await getDocs(eventsQuery);
        return eventsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as TrackingEvent[];
      } else {
        // Fallback to local storage
        return this.fallbackEvents.filter(event => event.lessonId === lessonId);
      }
    } catch (error) {
      console.error('Error getting lesson events:', error);
      // Fallback to local storage on error
      return this.fallbackEvents.filter(event => event.lessonId === lessonId);
    }
  }

  // Get all events for a student
  async getStudentEvents(studentId: string): Promise<TrackingEvent[]> {
    try {
      if (isFirebaseConfigured()) {
        const eventsQuery = query(
          collection(db, TRACKING_EVENTS_COLLECTION),
          where('studentId', '==', studentId),
          orderBy('timestamp', 'desc')
        );
        
        const eventsSnapshot = await getDocs(eventsQuery);
        return eventsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as TrackingEvent[];
      } else {
        // Fallback to local storage
        return this.fallbackEvents.filter(event => event.studentId === studentId);
      }
    } catch (error) {
      console.error('Error getting student events:', error);
      // Fallback to local storage on error
      return this.fallbackEvents.filter(event => event.studentId === studentId);
    }
  }

  // Get lesson summary
  async getLessonSummary(lessonId: string, startDate?: Date, endDate?: Date): Promise<{
    totalStudents: number;
    averageTime: number;
    averageScore: number;
    completionRate: number;
    engagementRate: number;
  }> {
    try {
      const lessonEvents = await this.getLessonEvents(lessonId, startDate, endDate);
      const studentIds = Array.from(new Set(lessonEvents.map(e => e.studentId)));
      
      if (studentIds.length === 0) {
        return {
          totalStudents: 0,
          averageTime: 0,
          averageScore: 0,
          completionRate: 0,
          engagementRate: 0
        };
      }

      const studentProgresses = await Promise.all(
        studentIds.map(id => this.getStudentProgress(lessonId, id))
      );
      const validProgresses = studentProgresses.filter(Boolean) as StudentProgress[];

      const totalTime = validProgresses.reduce((sum, p) => sum + p.timeSpent, 0);
      const totalScore = validProgresses.reduce((sum, p) => sum + (p.score || 0), 0);
      const completedCount = validProgresses.filter(p => p.completed).length;
      const totalInteractions = validProgresses.reduce((sum, p) => sum + p.interactions, 0);

      return {
        totalStudents: studentIds.length,
        averageTime: totalTime / studentIds.length,
        averageScore: totalScore / studentIds.length,
        completionRate: completedCount / studentIds.length,
        engagementRate: Math.min(totalInteractions / (studentIds.length * 10), 1)
      };
    } catch (error) {
      console.error('Error getting lesson summary:', error);
      return {
        totalStudents: 0,
        averageTime: 0,
        averageScore: 0,
        completionRate: 0,
        engagementRate: 0
      };
    }
  }

  // Export data in different formats
  async exportData(lessonId: string, format: 'csv' | 'json' | 'pdf' = 'json'): Promise<string> {
    try {
      const lessonEvents = await this.getLessonEvents(lessonId);
      const lessonSummary = await this.getLessonSummary(lessonId);

      const exportData = {
        lessonId,
        summary: lessonSummary,
        events: lessonEvents,
        timestamp: new Date().toISOString()
      };

      switch (format) {
        case 'json':
          return JSON.stringify(exportData, null, 2);
        
        case 'csv':
          return this.convertToCSV(lessonEvents);
        
        case 'pdf':
          return this.convertToPDF(exportData);
        
        default:
          return JSON.stringify(exportData);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      return JSON.stringify({ error: 'Failed to export data' });
    }
  }

  // Convert events to CSV format
  private convertToCSV(events: TrackingEvent[]): string {
    if (events.length === 0) return '';

    const headers = ['id', 'lessonId', 'studentId', 'eventType', 'timestamp', 'data'];
    const csvRows = [headers.join(',')];

    events.forEach(event => {
      // Handle both Date and Timestamp objects
      const timestamp = event.timestamp instanceof Date 
        ? event.timestamp.toISOString()
        : (event.timestamp as any).toDate().toISOString();

      const row = [
        event.id,
        event.lessonId,
        event.studentId,
        event.eventType,
        timestamp,
        JSON.stringify(event.data)
      ];
      csvRows.push(row.join(','));
    });

    return csvRows.join('\n');
  }

  // Convert to PDF (placeholder - would use a PDF library)
  private convertToPDF(data: any): string {
    // This would integrate with a PDF generation library
    return JSON.stringify(data);
  }

  // Check if event should be tracked based on configuration
  shouldTrackEvent(
    eventType: string,
    dataAndReporting: DataAndReporting
  ): boolean {
    if (!dataAndReporting.analytics_enabled) return false;

    const event = dataAndReporting.events?.find(e => e.when === eventType);
    return !!event;
  }

  // Get data fields to capture for an event
  getCaptureFields(
    eventType: string,
    dataAndReporting: DataAndReporting
  ): string[] {
    const event = dataAndReporting.events?.find(e => e.when === eventType);
    return event?.capture || [];
  }

  // Clear data for a lesson (for testing/reset)
  async clearLessonData(lessonId: string): Promise<void> {
    try {
      if (isFirebaseConfigured()) {
        // Clear Firebase data using batch write
        const batch = writeBatch(db);

        // Clear events
        const eventsQuery = query(
          collection(db, TRACKING_EVENTS_COLLECTION),
          where('lessonId', '==', lessonId)
        );
        const eventsSnapshot = await getDocs(eventsQuery);
        eventsSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });

        // Clear progress
        const progressQuery = query(
          collection(db, STUDENT_PROGRESS_COLLECTION),
          where('lessonId', '==', lessonId)
        );
        const progressSnapshot = await getDocs(progressQuery);
        progressSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });

        // Clear analytics
        const analyticsQuery = query(
          collection(db, ANALYTICS_COLLECTION),
          where('lessonId', '==', lessonId)
        );
        const analyticsSnapshot = await getDocs(analyticsQuery);
        analyticsSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });

        await batch.commit();
      } else {
        // Fallback to local storage clearing
        this.fallbackEvents = this.fallbackEvents.filter(e => e.lessonId !== lessonId);
        
        // Clear progress
        Array.from(this.fallbackProgress.keys()).forEach(key => {
          if (key.startsWith(lessonId)) {
            this.fallbackProgress.delete(key);
          }
        });
        
        // Clear analytics
        Array.from(this.fallbackAnalytics.keys()).forEach(key => {
          if (key.startsWith(lessonId)) {
            this.fallbackAnalytics.delete(key);
          }
        });
        
        this.persistEvents();
        this.persistProgress();
        this.persistAnalytics();
      }
    } catch (error) {
      console.error('Error clearing lesson data:', error);
      // Fallback to local storage clearing on error
      this.fallbackEvents = this.fallbackEvents.filter(e => e.lessonId !== lessonId);
      
      Array.from(this.fallbackProgress.keys()).forEach(key => {
        if (key.startsWith(lessonId)) {
          this.fallbackProgress.delete(key);
        }
      });
      
      Array.from(this.fallbackAnalytics.keys()).forEach(key => {
        if (key.startsWith(lessonId)) {
          this.fallbackAnalytics.delete(key);
        }
      });
      
      this.persistEvents();
      this.persistProgress();
      this.persistAnalytics();
    }
  }

  // Persistence methods (for fallback localStorage)
  private persistEvents(): void {
    try {
      localStorage.setItem('tracking_events', JSON.stringify(this.fallbackEvents));
    } catch (error) {
      console.warn('Failed to persist tracking events:', error);
    }
  }

  private persistProgress(): void {
    try {
      const progressArray = Array.from(this.fallbackProgress.values());
      localStorage.setItem('tracking_progress', JSON.stringify(progressArray));
    } catch (error) {
      console.warn('Failed to persist tracking progress:', error);
    }
  }

  private persistAnalytics(): void {
    try {
      const analyticsArray = Array.from(this.fallbackAnalytics.values());
      localStorage.setItem('tracking_analytics', JSON.stringify(analyticsArray));
    } catch (error) {
      console.warn('Failed to persist tracking analytics:', error);
    }
  }

  // Load persisted data (for fallback localStorage)
  loadPersistedData(): void {
    try {
      // Only load from localStorage if Firebase is not configured
      if (!isFirebaseConfigured()) {
        const eventsData = localStorage.getItem('tracking_events');
        if (eventsData) {
          this.fallbackEvents = JSON.parse(eventsData).map((e: any) => ({
            ...e,
            timestamp: new Date(e.timestamp)
          }));
        }

        const progressData = localStorage.getItem('tracking_progress');
        if (progressData) {
          const progressArray = JSON.parse(progressData);
          this.fallbackProgress.clear();
          progressArray.forEach((p: StudentProgress) => {
            const key = `${p.lessonId}-${p.studentId}`;
            this.fallbackProgress.set(key, {
              ...p,
              lastActivity: p.lastActivity instanceof Date 
                ? p.lastActivity 
                : typeof p.lastActivity === 'string' 
                  ? new Date(p.lastActivity)
                  : (p.lastActivity as any).toDate ? (p.lastActivity as any).toDate() : new Date()
            });
          });
        }

        const analyticsData = localStorage.getItem('tracking_analytics');
        if (analyticsData) {
          const analyticsArray = JSON.parse(analyticsData);
          this.fallbackAnalytics.clear();
          analyticsArray.forEach((a: AnalyticsData) => {
            const key = `${a.lessonId}-${a.studentId}`;
            this.fallbackAnalytics.set(key, a);
          });
        }
      }
    } catch (error) {
      console.warn('Failed to load persisted tracking data:', error);
    }
  }
}

// Create singleton instance
export const trackingService = new TrackingService();

// Load persisted data on initialization
trackingService.loadPersistedData();
